import { useEffect, useState } from 'react'
import './App.css'
import type { Set } from './types/Set'
import FilterBar from './components/FilterBar'
import SetList from './components/SetList'
import { CategoryInput } from './components/CategoryInput'
import { KeywordsInput } from './components/KeywordsInput'

function App() {
  const isDev = import.meta.env.DEV
  const [sets, setSets] = useState<Set[]>([])
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showAdmin, setShowAdmin] = useState(false)

  useEffect(() => {
    fetch('/data/sets.json')
      .then((r) => r.json())
      .then((data: Set[]) => setSets(data))
      .catch(() => {
        // gracefully degrade: leave sets empty
      })
  }, [])

  const q = search.trim().toLowerCase()
  const filtered = sets.filter((s) => {
    const catArr = Array.isArray(s.category) ? s.category : s.category ? [s.category] : []
    if (selectedCategory && !catArr.includes(selectedCategory)) return false
    if (!q) return true
    return (
      s.name.toLowerCase().includes(q) ||
      catArr.some((c) => c.toLowerCase().includes(q)) ||
      (s.keywords || []).some((k) => k.toLowerCase().includes(q))
    )
  }).sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }))

  // Flatten all categories for filter bar
  const categories = Array.from(
    new Set(
      sets.flatMap((s) => Array.isArray(s.category) ? s.category : s.category ? [s.category] : [])
    )
  ).sort((a, b) => a.localeCompare(b))
  const totalPieces = sets.reduce((sum, set) => sum + (set.parts || 0), 0)
  const [activeSet, setActiveSet] = useState<Set | null>(null)
  const [draft, setDraft] = useState<Set | null>(null)
  const [saving, setSaving] = useState(false)
  const [showAddPopover, setShowAddPopover] = useState(false)
  const [importSetId, setImportSetId] = useState('')
  const [importing, setImporting] = useState(false)

  return (
    <div className="app-root">
      <header className="nav-bar">
        <div className="brand">
          <span className="dot" aria-hidden="true" />
          <span className="brand-name">lego</span>
        </div>
        <div className="nav-spacer"></div>
        <div className="nav-stats">
          <span>{sets.length} sets</span>
          <span className="divider">•</span>
          <span>{totalPieces.toLocaleString()} pieces</span>
        </div>
        {isDev && (
          <button className="ghost-button" onClick={() => setShowAdmin((s) => !s)}>
            {showAdmin ? 'Hide Admin (dev)' : 'Show Admin (dev)'}
          </button>
        )}
      </header>

      <main>
        <section className="panel">
          <FilterBar
            search={search}
            setSearch={setSearch}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
          />
        </section>

        <section className="panel">
          <SetList
            sets={filtered}
            onSelect={(set) => {
              setActiveSet(set)
              // ensure defaults for optional fields to avoid uncontrolled inputs
              setDraft({
                ...set,
                category: set.category || '',
                subtheme: set.subtheme || '',
                ageRange: set.ageRange || '',
                location: set.location || '',
                instructionsUrl: set.instructionsUrl || '',
                instructionsPdf: set.instructionsPdf || '',
                image: set.image || '',
                productUrl: set.productUrl || '',
                keywords: set.keywords || []
              })
            }}
          />
        </section>

      </main>

      <footer className="footer">
        <span>lego.jamie.gg</span>
      </footer>

      {isDev && (
        <>
          <button 
            className="floating-add-button" 
            onClick={() => setShowAddPopover(!showAddPopover)}
          >
            +
          </button>
          {showAddPopover && (
            <div className="add-popover">
              <h4>Add New Set</h4>
              <div className="set-card-footer">
                <button
                  onClick={() => {
                    const newSet: Set = {
                      id: '',
                      name: 'New Set',
                      category: '',
                      subtheme: '',
                      ageRange: '',
                      location: '',
                      instructionsUrl: '',
                      instructionsPdf: '',
                      image: '',
                      productUrl: '',
                      keywords: []
                    }
                    setSets([...sets, newSet])
                    setActiveSet(newSet)
                    setDraft(newSet)
                    setShowAddPopover(false)
                  }}
                >
                  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Create Blank
                </button>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>
                  Import from Rebrickable
                </label>
                <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Set ID (e.g., 75192-1)"
                    value={importSetId}
                    onChange={(e) => setImportSetId(e.target.value)}
                    className="popover-input"
                  />
                  <button
                    className="popover-import-button"
                    disabled={!importSetId.trim() || importing}
                    onClick={async () => {
                      const id = importSetId.trim()
                      if (!id) return
                      setImporting(true)
                      try {
                        const res = await fetch(`/dev-api/rebrickable-set?q=${encodeURIComponent(id)}`)
                        const data = await res.json()
                        if (!res.ok) {
                          const message = data?.detail || data?.error || 'Failed to import'
                          throw new Error(message)
                        }
                        const imported = data.set as Partial<Set>
                        const newSet: Set = {
                          id: id,
                          name: imported.name || 'Imported Set',
                          category: imported.category,
                          subtheme: imported.subtheme,
                          ageRange: '',
                          year: imported.year,
                          parts: imported.parts,
                          image: imported.image,
                          instructionsUrl: imported.instructionsUrl,
                          productUrl: imported.productUrl,
                          keywords: imported.keywords || [],
                          location: '',
                          instructionsPdf: ''
                        }
                        setSets([...sets, newSet])
                        setActiveSet(newSet)
                        setDraft(newSet)
                        setShowAddPopover(false)
                        setImportSetId('')
                      } catch (err) {
                        alert('Import failed: ' + String(err))
                      } finally {
                        setImporting(false)
                      }
                    }}
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeSet && draft && (
        <div className="modal-backdrop" onClick={() => setActiveSet(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0 }}>{draft.name}</h3>
                {draft.ageRange && <p style={{ margin: '0.15rem 0 0', color: '#475569' }}>{draft.ageRange}</p>}
              </div>
              <button className="ghost-button" onClick={() => setActiveSet(null)}>
                Close
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-grid">
                <label className="admin-field">
                  <span>Set ID</span>
                  <input value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })} />
                </label>
                <label className="admin-field">
                  <span>Name</span>
                  <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                </label>
                <label className="admin-field">
                  <span>Category (CSV for multiple)</span>
                  <CategoryInput value={draft.category} onChange={cat => setDraft({ ...draft, category: cat })} />
                </label>
                <label className="admin-field">
                  <span>Subtheme</span>
                  <input value={draft.subtheme || ''} onChange={(e) => setDraft({ ...draft, subtheme: e.target.value || undefined })} />
                </label>
                <label className="admin-field">
                  <span>Age range</span>
                  <input value={draft.ageRange || ''} onChange={(e) => setDraft({ ...draft, ageRange: e.target.value || undefined })} />
                </label>
                <label className="admin-field">
                  <span>Parts</span>
                  <input
                    type="number"
                    min="0"
                    value={draft.parts ?? ''}
                    onChange={(e) => setDraft({ ...draft, parts: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </label>
                <label className="admin-field">
                  <span>Location</span>
                  <input value={draft.location || ''} onChange={(e) => setDraft({ ...draft, location: e.target.value || undefined })} />
                </label>
                <label className="admin-field">
                  <span>Keywords</span>
                  <KeywordsInput value={draft.keywords} onChange={kw => setDraft({ ...draft, keywords: kw })} />
                </label>
              </div>
              <div className="modal-grid">
                <label className="admin-field">
                  <span>Image URL</span>
                  <input value={draft.image || ''} onChange={(e) => setDraft({ ...draft, image: e.target.value || undefined })} />
                </label>
                <label className="admin-field">
                  <span>Rebrickable URL</span>
                  <input
                    value={
                      draft.productUrl && draft.productUrl.includes('rebrickable.com')
                        ? draft.productUrl
                        : draft.id
                          ? `https://rebrickable.com/sets/${draft.id}/`
                          : ''
                    }
                    onChange={(e) => setDraft({ ...draft, productUrl: e.target.value || undefined })}
                  />
                </label>
                <label className="admin-field">
                  <span>Instructions URL</span>
                  <input
                    value={draft.instructionsUrl || ''}
                    onChange={(e) => setDraft({ ...draft, instructionsUrl: e.target.value || undefined })}
                    placeholder={draft.id ? `https://www.lego.com/en-us/service/buildinginstructions/${draft.id}` : ''}
                  />
                </label>
                <label className="admin-field">
                  <span>Instructions PDF (local)</span>
                  <input
                    value={draft.instructionsPdf || ''}
                    onChange={(e) => setDraft({ ...draft, instructionsPdf: e.target.value || undefined })}
                  />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <div className="modal-links" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
                {draft.instructionsUrl && (
                  <a href={draft.instructionsUrl} target="_blank" rel="noreferrer" className="set-card-footer-link">
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="link-icon"><path d="M14 3h7v7" /><path d="M10 14 21 3" /><path d="M21 14v7H3V3h7" /></svg> Manual
                  </a>
                )}
                {draft.id && (
                  <a href={`https://www.lego.com/en-us/product/${encodeURIComponent(draft.id)}`} target="_blank" rel="noreferrer" className="set-card-footer-link">
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="link-icon"><path d="M14 3h7v7" /><path d="M10 14 21 3" /><path d="M21 14v7H3V3h7" /></svg> Lego
                  </a>
                )}
                {draft.id && (
                  <a href={`https://rebrickable.com/sets/${encodeURIComponent(draft.id)}/`} target="_blank" rel="noreferrer" className="set-card-footer-link">
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="link-icon"><path d="M14 3h7v7" /><path d="M10 14 21 3" /><path d="M21 14v7H3V3h7" /></svg> Rebrickable
                  </a>
                )}
                {draft.instructionsPdf && (
                  <a href={draft.instructionsPdf} target="_blank" rel="noreferrer" className="set-card-footer-link">
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="link-icon"><path d="M14 3h7v7" /><path d="M10 14 21 3" /><path d="M21 14v7H3V3h7" /></svg> PDF
                  </a>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {isDev && (
                  <>
                    <button
                      className="danger-button"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete "${draft.name}"?`)) {
                          const nextSets = sets.filter((s) => s.id !== activeSet.id)
                          setSets(nextSets)
                          fetch('/dev-api/sets', {
                            method: 'POST',
                            headers: { 'content-type': 'application/json' },
                            body: JSON.stringify(nextSets)
                          }).then(() => {
                            setActiveSet(null)
                            alert('Set deleted')
                          }).catch((err) => alert('Delete failed: ' + String(err)))
                        }
                      }}
                    >
                      Delete
                    </button>
                    <button
                      className="primary-button"
                      disabled={saving}
                      onClick={async () => {
                        if (!draft) return
                        setSaving(true)
                        try {
                          const nextSets = sets.map((s) => (s.id === activeSet.id ? draft : s))
                          setSets(nextSets)
                          const res = await fetch('/dev-api/sets', {
                            method: 'POST',
                            headers: { 'content-type': 'application/json' },
                            body: JSON.stringify(nextSets)
                          })
                          if (!res.ok) {
                            throw new Error(await res.text())
                          }
                          alert('Saved updated set to sets.json')
                        } catch (err) {
                          alert('Save failed: ' + String(err))
                        } finally {
                          setSaving(false)
                        }
                      }}
                    >
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default App
